import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';

const SidebarContext = createContext();

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};

export const SidebarProvider = ({ children }) => {
  const [activeMenu, setActiveMenu] = useState(null);
  const location = useLocation();
  const { user } = useAuth();

  // تعریف منو HR (یکسان برای تمام route های HR)
  const hrMenu = {
    key: 'hr',
    label: 'منابع انسانی',
    items: [
      { label: 'داشبورد', path: '/hr/dashboard' },
      { label: 'دپارتمان', path: '/hr/departments' },
      { label: 'سمت شغلی', path: '/hr/positions' },
      { label: 'کارمندان', path: '/hr/employees' },
      { label: 'برنامه شیفت‌ها', path: '/hr/shifts' },
      { label: 'مرخصی‌ها', path: '/hr/leaves' },
      { label: 'کارتابل تأییدات', path: '/workflow/inbox' },
      { label: 'حضور و غیاب', path: '/hr/attendance' },
      { label: 'اصلاح تردد', path: '/hr/attendance-regularizations' },
      { label: 'گزارش اصلاحات تردد', path: '/hr/attendance-adjustments' },
      { label: 'اضافه‌کاری', path: '/hr/overtime-requests' },
      { label: 'مأموریت', path: '/hr/attendance-missions' },
      { label: 'تعطیلی', path: '/hr/holidays' },
      { label: 'تنظیمات مرخصی', path: '/hr/leaves-settings' },
      { label: 'تنظیمات حضور غیاب', path: '/hr/attendance-settings' },
      // { label: 'قدردانی', path: '/hr/appreciations' },
    ],
  };

  const financeMenu = {
    key: 'finance',
    label: 'مدیریت مالی و خزانه',
    items: [
      { label: 'داشبورد و کنترل مالی', path: '/finance/dashboard' },
      { label: 'کارتابل تایید واریزی‌ها', path: '/finance/approvals' },
      { label: 'مرکز تراکنش‌ها', path: '/finance/transactions' },
      { label: 'خزانه چک صیادی', path: '/finance/cheques' },
      { label: 'گزارش مطالبات', path: '/finance/receivables-summary' },
      { label: 'گزارش سنی بدهی', path: '/finance/aging-report' },
      { label: 'لاگ‌های ممیزی', path: '/finance/audit-logs' },
      { label: 'ثبت واریزی معامله', path: '/finance/register-payment' },
      { label: 'حساب بانکی', path: '/finance/bank-accounts' },
      { label: 'تنخواه‌گردان', path: '/finance/petty-cash' },
      { label: 'هزینه‌ها', path: '/finance/expenses' },
      { label: 'هزینه‌های دوره‌ای', path: '/finance/recurring-expenses' },
      { label: 'تارگت‌ها', path: '/finance/targets' },
      { label: 'طرح‌های پورسانت', path: '/finance/commission-plans' },
      { label: 'فیش حقوقی', path: '/finance/payroll' },
      { label: 'پیش‌فاکتورها', path: '/finance/estimates' },
      { label: 'فاکتورها', path: '/finance/invoices' },
      { label: 'درگاه پرداخت', path: '/finance/payment-gateway' },
      { label: 'مالیات', path: '/finance/tax' },
      { label: 'تنظیمات مالی', path: '/finance/invoice-settings' },
      { label: 'عودت وجه', path: '/finance/refunds' },
      { label: 'سقف تخفیف', path: '/finance/discount-limits' },
      { label: 'پروفایل مالی مشتری ۳۶۰', path: '/finance/client-profile' },
    ],
  };

  const leadsMenu = {
    key: 'leads',
    label: 'سرنخ‌ها',
    items: [
      { label: 'سرنخ‌ها', path: '/leads' },
      { label: 'معامله‌ها', path: '/leads/deals' },
      { label: 'فرم ساز', path: '/leads/lead-forms' },
      { label: 'گزارش', path: '/leads/reports' },
      { label: 'تنظیمات', path: '/leads/settings' },
    ],
  };

  // تشخیص activeMenu بر اساس route فعلی
  useEffect(() => {
    const pathname = location.pathname;

    // داشبورد اصلی: بدون سایدبار ماژول
    if (pathname === '/dashboard') {
      setActiveMenu(null);
      return;
    }

    if (pathname === '/dashboard/super-admin') {
      setActiveMenu({
        key: 'superadmin-dashboard',
        label: 'داشبورد',
        items: [{ label: 'داشبورد سوپرادمین', path: '/dashboard/super-admin' }],
      });
      return;
    }
    
    // داشبورد
    if (pathname.startsWith('/dashboard/')) {
      setActiveMenu({
        key: 'dashboard',
        label: 'داشبورد',
        items: [
          { label: 'داشبورد خصوصی', path: '/dashboard/private' },
          { label: 'داشبورد پیشرفته', path: '/dashboard/advanced' },
        ],
      });
      return;
    }
    
    // اگر route مربوط به HR است
    if (pathname === '/hr' || pathname.startsWith('/hr/')) {
      setActiveMenu(hrMenu);
      return;
    }

    // کارتابل تأییدات عمومی ERP
    if (pathname.startsWith('/workflow')) {
      setActiveMenu({
        key: 'workflow',
        label: 'فرآیندهای تأیید',
        items: [
          { label: 'کارتابل تأییدات', path: '/workflow/inbox' },
          { label: 'مرخصی‌ها', path: '/hr/leaves' },
          { label: 'اضافه‌کاری', path: '/hr/overtime-requests' },
          { label: 'مأموریت', path: '/hr/attendance-missions' },
          { label: 'اصلاح تردد', path: '/hr/attendance-regularizations' },
        ],
      });
      return;
    }
    
    // اگر route مربوط به finance است
    if (pathname.startsWith('/finance/')) {
      setActiveMenu(financeMenu);
      return;
    }
    
    // اگر route مربوط به leads یا deals است
    if (pathname.startsWith('/leads') || pathname.startsWith('/deals')) {
      setActiveMenu(leadsMenu);
      return;
    }

    if (pathname.startsWith('/products')) {
      setActiveMenu({
        key: 'products',
        label: 'محصولات',
        items: [
          { label: 'لیست محصولات', path: '/products' },
          { label: 'محصول جدید', path: '/products/add' },
        ],
      });
      return;
    }

    if (pathname.startsWith('/orders')) {
      setActiveMenu({
        key: 'orders',
        label: 'سفارشات',
        items: [
          { label: 'لیست سفارشات', path: '/orders' },
          { label: 'سفارش جدید', path: '/orders/add' },
        ],
      });
      return;
    }

    if (pathname.startsWith('/clients')) {
      setActiveMenu({
        key: 'clients',
        label: 'مشتریان',
        items: [
          { label: 'لیست مشتریان', path: '/clients', permission: 'view_clients' },
          { label: 'مشتری جدید', path: '/clients/add', permission: 'add_clients' },
        ],
      });
      return;
    }
    
    // اگر route مربوط به تیکت‌ها است
    if (pathname.startsWith('/tickets')) {
      setActiveMenu({
        key: 'tickets',
        label: 'تیکت‌ها',
        items: [
          { label: 'لیست تیکت‌ها', path: '/tickets', permission: 'view_tickets' },
          { label: 'تیکت جدید', path: '/tickets/add', permission: 'add_tickets' },
          { label: 'تنظیمات تیکت', path: '/tickets/settings', permission: 'manage_ticket_setting' },
          { label: 'فرم تیکت مشتری', path: '/tickets/ticket-form', permission: 'manage_ticket_setting' },
        ],
      });
      return;
    }

    if (pathname.startsWith('/call-center')) {
      setActiveMenu({
        key: 'call-center',
        label: 'مرکز تماس',
        items: [
          { label: 'تماس‌ها', path: '/call-center/calls', permission: 'view_callcenter' },
          { label: 'اپراتورها', path: '/call-center/agents', permission: 'manage_callcenter_agents' },
          { label: 'گزارش‌ها', path: '/call-center/reports', permission: 'view_callcenter_reports' },

          { label: 'تنظیمات', path: '/call-center/settings', permission: 'manage_callcenter_settings' },
          // { label: 'لیست سیاه', path: '/call-center/blacklist', permission: 'manage_callcenter_blacklist' },
        ],
      });
      return;
    }

    if (pathname.startsWith('/notices')) {
      setActiveMenu({
        key: 'notices',
        label: 'اعلانات',
        items: [
          { label: 'لیست اعلانات', path: '/notices', permission: 'view_notice' },
          { label: 'اعلان جدید', path: '/notices/add', permission: 'add_notice' },
        ],
      });
      return;
    }

    if (pathname.startsWith('/messages')) {
      setActiveMenu({
        key: 'messages',
        label: 'پیام‌ها',
        items: [
          { label: 'لیست پیام‌ها', path: '/messages', permission: 'view_message' },
        ],
      });
      return;
    }

    if (pathname.startsWith('/settings/superadmin/')) {
      setActiveMenu({
        key: 'superadmin-settings',
        label: 'تنظیمات',
        items: [
          { label: 'تنظیمات برنامه', path: '/settings/superadmin/app-settings' },
          { label: 'تنظیمات پروفایل', path: '/settings/superadmin/profile' },
          { label: 'تنظیمات مالی', path: '/settings/superadmin/invoice' },
          { label: 'تنظیمات زبان', path: '/settings/superadmin/language' },
          { label: 'تنظیمات ارز', path: '/settings/superadmin/currency' },
        ],
      });
      return;
    }

    if (pathname.startsWith('/settings')) {
      const isSuperAdmin = Number(user?.is_superadmin) === 1;
      setActiveMenu({
        key: isSuperAdmin ? 'superadmin-panel' : 'settings',
        label: isSuperAdmin ? 'پنل سوپر ادمین' : 'تنظیمات',
        items: isSuperAdmin
          ? [
              { label: 'بسته‌ها', path: '/settings/packages' },
              { label: 'شرکت‌ها', path: '/settings/companies' },
              { label: 'صورتحساب', path: '/settings/superadmin-invoices' },
              { label: 'سوالات متداول مدیر', path: '/settings/superadmin-faqs' },
              { label: 'سوپر ادمین', path: '/settings/superadmin-users' },
              { label: 'تیکت پشتیبانی', path: '/settings/support-tickets' },
            ]
          : [
              { label: 'تنظیمات شرکت', path: '/settings/company', permission: 'manage_settings' },
              { label: 'نشانی کسب‌وکار', path: '/settings/business-address', permission: 'manage_settings' },
              { label: 'تنظیمات ارز', path: '/settings/currency-settings', permission: 'manage_settings' },
              { label: 'تنظیمات پروفایل', path: '/settings/profile-settings' },
              { label: 'تنظیمات اپلیکیشن', path: '/settings/app-settings', permission: 'manage_settings' },
              { label: 'نقش‌ها و دسترسی‌ها', path: '/settings/role-permissions', permission: 'manage_settings' },
            ],
      });
      return;
    }

    if (pathname.startsWith('/reports')) {
      setActiveMenu({
        key: 'reports',
        label: 'گزارشات',
        items: [
          { label: 'نمای کلی گزارشات', path: '/reports' },
          { label: 'گزارش مالی', path: '/reports/finance' },
          { label: 'درآمد در مقابل هزینه', path: '/reports/income-expense' },
          { label: 'گزارش مرخصی', path: '/reports/leaves' },
          { label: 'سهمیه مرخصی کارکنان', path: '/reports/leave-quota' },
          { label: 'گزارش حضور و غیاب', path: '/reports/attendance' },
          { label: 'گزارش هزینه‌ها', path: '/reports/expenses' },
          { label: 'گزارش معاملات', path: '/reports/leads' },
          { label: 'گزارش فروش', path: '/reports/sales' },
        ],
      });
      return;
    }

    if (pathname.startsWith('/work')) {
      setActiveMenu({
        key: 'work',
        label: 'کار',
        items: [
          { label: 'وظایف', path: '/work/tasks' },
          { label: 'پروژه‌ها', path: '/work/projects' },
          { label: 'الگوهای پروژه', path: '/work/projects/templates' },
          { label: 'قراردادها', path: '/work/contracts' },
          { label: 'قالب‌های قرارداد', path: '/work/contracts/templates' },
        ],
      });
      return;
    }

    // اگر route مربوط به calendar است
    if (pathname.startsWith('/calendar')) {
      setActiveMenu({
        key: 'calendar',
        label: 'تقویم من',
        items: [
          { label: 'تقویم من', path: '/calendar' },
        ],
      });
      return;
    }
    
    // اگر route مربوط به action است
    if (pathname.startsWith('/action')) {
      setActiveMenu({
        key: 'action',
        label: 'اقدام',
        items: [
          { label: 'داشبورد', path: '/action/dashboard' },
          // { label: 'صفحه اصلی', path: '/action' },
          { label: 'اقدام‌های من', path: '/action/my-actions' },
          { label: 'ساخت اقدام', path: '/action/create' },
          { label: 'اعلان‌ها', path: '/action/notifications' },
          { label: 'تنظیمات اعلان', path: '/action/notifications/settings' },
          { label: 'گزارشات', path: '/action/reports' },
        ],
      });
      return;
    }
    
    // در مسیرهای ناشناخته، وضعیت فعلی سایدبار حفظ می‌شود.
  }, [location.pathname, user?.is_superadmin]);

  const setActiveMenuManually = (menu) => {
    setActiveMenu(menu);
  };

  const closeSidebar = () => {
    setActiveMenu(null);
  };

  return (
    <SidebarContext.Provider
      value={{
        activeMenu,
        setActiveMenu: setActiveMenuManually,
        closeSidebar,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
};

export default SidebarContext;
